import React, { useEffect, useState } from "react";
import {
  Typography,
  Paper,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { useApi } from "../../utils/api";

const formatTxCurrency = (amount, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);

const formatTxDate = (timestamp) =>
  moment.unix(timestamp).format("MMM D, YYYY");

const statusChipSx = (theme, status) => {
  const s = (status || "").toLowerCase();
  const base = {
    borderRadius: 999,
    fontWeight: 600,
    textTransform: "capitalize",
    "& .MuiChip-label": { px: 1.25 },
  };
  if (s === "succeeded" || s === "paid") {
    return {
      ...base,
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    };
  }
  if (s === "failed") {
    return {
      ...base,
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    };
  }
  if (s === "pending") {
    return {
      ...base,
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.dark,
    };
  }
  if (s === "canceled") {
    return {
      ...base,
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.grey[900],
    };
  }
  return base;
};

/**
 * Fetches and displays Stripe charges for a client (self or coach viewing that client).
 */
const ClientSubscriptionTransactions = ({
  profileId,
  title = "Your payments",
  paperSx = {},
}) => {
  const theme = useTheme();
  const { getMyTransactions } = useApi();
  const [myTransactions, setMyTransactions] = useState([]);
  const [txError, setTxError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setTxError("");
      try {
        const start = moment().subtract(365, "days").format("YYYY-MM-DD");
        const txRes = await getMyTransactions({
          userId: profileId,
          startDate: start,
          limit: 50,
        });
        if (cancelled) return;
        if (txRes?.error) {
          setTxError(
            typeof txRes.error === "string"
              ? txRes.error
              : txRes.message || "Could not load payment history"
          );
          setMyTransactions([]);
        } else {
          setMyTransactions(txRes?.data || []);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setTxError(e?.message || "Could not load payment history");
          setMyTransactions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  return (
    <Paper sx={{ p: 2, mt: 2, ...paperSx }}>
      <Typography sx={{ mb: 2 }}>
        <b>{title}</b>
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {txError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {txError}
            </Typography>
          )}
          {!txError && myTransactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No card payments in the last 12 months.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myTransactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>{formatTxDate(tx.created)}</TableCell>
                      <TableCell>{tx.description || "—"}</TableCell>
                      <TableCell align="right">
                        {formatTxCurrency(tx.amount, tx.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={tx.status}
                          sx={statusChipSx(theme, tx.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Paper>
  );
};

export default ClientSubscriptionTransactions;
